import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CAROUSEL1_BEFORE, CAROUSEL1_AFTER, 
  CAROUSEL2_BEFORE, CAROUSEL2_AFTER,
  CAROUSEL3_BEFORE, CAROUSEL3_AFTER,
  CAROUSEL4_BEFORE, CAROUSEL4_AFTER
} from '../assets/carouselImages';

// HARDCODED PROJECTS with embedded images for fast loading
// Order: Interior Painting first (cabinet project), then others
const HARDCODED_PROJECTS = [
  {
    name: 'Interior Painting',
    category: 'interior-painting',
    before: {
      file_url: CAROUSEL1_BEFORE,
      title: 'Kitchen Cabinet Refinishing - Before'
    },
    after: {
      file_url: CAROUSEL1_AFTER,
      title: 'Kitchen Cabinet Refinishing - After'
    }
  },
  {
    name: 'General Repairs',
    category: 'general-repairs',
    before: {
      file_url: CAROUSEL2_BEFORE,
      title: 'Ceiling Repair - Before'
    },
    after: {
      file_url: CAROUSEL2_AFTER,
      title: 'Ceiling Repair - After'
    }
  },
  {
    name: 'Carpentry',
    category: 'carpentry',
    before: {
      file_url: CAROUSEL3_BEFORE,
      title: 'Entertainment Center - Before'
    },
    after: {
      file_url: CAROUSEL3_AFTER,
      title: 'Built-In Shelving - After'
    }
  },
  {
    name: 'Plumbing',
    category: 'plumbing',
    before: {
      file_url: CAROUSEL4_BEFORE,
      title: 'Concrete Floor Demolition - Before'
    },
    after: {
      file_url: CAROUSEL4_AFTER,
      title: 'New Concrete Floor - After'
    }
  }
];

const FeaturedWorkCarousel = () => {
  // Use hardcoded projects directly for fast loading - no API calls
  const [projects] = useState(HARDCODED_PROJECTS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Helper to get full image URL
  const getImageUrl = (fileUrl) => {
    if (!fileUrl) return '';
    return fileUrl;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'exterior-painting': 'Exterior Painting',
      'interior-painting': 'Interior Painting',
      'general-repairs': 'General Repairs',
      'carpentry': 'Carpentry',
      'plumbing': 'Plumbing',
      'electrical': 'Electrical',
      'building-maintenance': 'Building Maintenance'
    };
    return labels[category] || category?.replace(/-/g, ' ')?.replace(/\b\w/g, c => c.toUpperCase()) || category;
  };

  const nextSlide = useCallback(() => {
    if (projects.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % projects.length);
    }
  }, [projects.length]);

  const prevSlide = () => {
    if (projects.length > 0) {
      setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length);
    }
  };

  // Auto-advance carousel
  useEffect(() => {
    if (projects.length <= 1 || isPaused) return;
    
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [projects.length, isPaused, nextSlide]);

  const getCategoryRoute = (category) => {
    const routes = {
      'exterior-painting': '/exterior-painting',
      'interior-painting': '/interior-painting',
      'general-repairs': '/general-repairs',
      'carpentry': '/carpentry',
      'plumbing': '/plumbing',
      'electrical': '/electrical',
      'building-maintenance': '/building-maintenance'
    };
    return routes[category] || '/book';
  };

  if (loading) {
    return (
      <div className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse flex flex-col items-center">
            <div className="h-8 bg-slate-700 rounded w-64 mb-4"></div>
            <div className="h-4 bg-slate-700 rounded w-96 mb-8"></div>
            <div className="grid grid-cols-2 gap-4 w-full max-w-4xl">
              <div className="h-64 bg-slate-700 rounded-xl"></div>
              <div className="h-64 bg-slate-700 rounded-xl"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return null; // Don't show section if no before/after pairs
  }

  const currentProject = projects[currentIndex];

  return (
    <section 
      className="py-16 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="featured-work-carousel"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge className="bg-brand-teal/20 text-brand-teal hover:bg-brand-teal/30 mb-4">
            Featured Transformations
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            See the Difference We Make
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Real results from real projects. Swipe through our before & after gallery.
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative max-w-5xl mx-auto">
          {/* Navigation Arrows */}
          {projects.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-3 rounded-full text-white transition-all hover:scale-110"
                aria-label="Previous project"
                data-testid="carousel-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-3 rounded-full text-white transition-all hover:scale-110"
                aria-label="Next project"
                data-testid="carousel-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Before/After Display */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Before Image */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-red-500/30 rounded-2xl blur-lg opacity-50 group-hover:opacity-70 transition-opacity"></div>
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={getImageUrl(currentProject.before.file_url)}
                  alt={`Before - ${currentProject.name}`}
                  className="w-full h-56 sm:h-64 md:h-72 object-cover transition-transform duration-500 group-hover:scale-105"
                  data-testid="featured-before-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <Badge className="bg-red-500 text-white font-bold px-4 py-1 text-sm">
                    BEFORE
                  </Badge>
                </div>
              </div>
            </div>

            {/* After Image */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-green-500/30 rounded-2xl blur-lg opacity-50 group-hover:opacity-70 transition-opacity"></div>
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={getImageUrl(currentProject.after.file_url)}
                  alt={`After - ${currentProject.name}`}
                  className="w-full h-56 sm:h-64 md:h-72 object-cover transition-transform duration-500 group-hover:scale-105"
                  data-testid="featured-after-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <Badge className="bg-green-500 text-white font-bold px-4 py-1 text-sm">
                    AFTER
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Project Info */}
          <div className="mt-6 text-center">
            <h3 className="text-xl font-bold text-white mb-1">
              {currentProject.name}
            </h3>
            <p className="text-brand-teal text-sm mb-4">
              {getCategoryLabel(currentProject.category)}
            </p>
            <Button
              onClick={() => window.location.href = getCategoryRoute(currentProject.category)}
              className="bg-gradient-to-r from-brand-blue to-brand-teal hover:from-brand-blue-dark hover:to-brand-teal-dark text-white"
              data-testid="featured-view-more"
            >
              View More {getCategoryLabel(currentProject.category)}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Dots Indicator */}
          {projects.length > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {projects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex 
                      ? 'bg-brand-teal w-8' 
                      : 'bg-slate-600 hover:bg-slate-500'
                  }`}
                  aria-label={`Go to project ${index + 1}`}
                  data-testid={`carousel-dot-${index}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedWorkCarousel;
